import { LitElement, css, html, nothing } from 'lit';
import { shared } from './shared.ts';
import { backStep, completeStep, skipFlow, startFlow, type FlowState } from './walkthrough-flow.ts';

/**
 * One step of a walkthrough. Without `button` it is a doing step: the kid does the real move and
 * the activity calls `complete(id)`. With `button` it is a talking step; the button advances it,
 * unless `waitForActivity` is set (then it fires `ka-walkthrough-action` and waits for `complete`).
 */
export interface WalkthroughStep {
  id: string;
  say: string;
  hint?: string;
  /** What to spotlight. Missing or null: the whole screen dims. */
  target?: () => Element | null | undefined;
  button?: string;
  waitForActivity?: boolean;
  /** Extra controls inside the card (a name field…). */
  body?: () => unknown;
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Teaches an activity on its real screen: dims everything but the target and shows a coach card.
 * The screen underneath stays live; the activity ignores input that does not belong to the step.
 */
export class KaWalkthrough extends LitElement {
  static override properties = {
    steps: { attribute: false },
    open: { type: Boolean, reflect: true },
    placement: {},
    flow: { state: true },
    box: { state: true },
    cardTop: { state: true },
  };
  declare steps: WalkthroughStep[];
  declare open: boolean;
  /** 'top' pins the card to the top, for activities that use the bottom of the screen. */
  declare placement: 'auto' | 'top';
  declare flow: FlowState | null;
  declare box: Box | null;
  declare cardTop: boolean;
  private raf = 0;
  private shown = -1;

  static override styles = [
    shared,
    css`
      :host {
        display: contents;
      }
      .layer {
        position: fixed;
        inset: 0;
        z-index: 50;
        pointer-events: none;
      }
      .dim {
        position: fixed;
        inset: 0;
        background: rgb(0 0 0 / 0.55);
      }
      .hole {
        position: fixed;
        box-shadow: 0 0 0 100vmax rgb(0 0 0 / 0.55);
        outline: 3px solid var(--ka-focus);
      }
      .card {
        pointer-events: auto;
        position: fixed;
        left: 16px;
        right: 16px;
        bottom: 16px;
        max-width: 32rem;
        margin: 0 auto;
        box-shadow: var(--ka-lift);
        display: grid;
        gap: 0.5rem;
      }
      .card.top {
        top: 16px;
        bottom: auto;
      }
      .dots {
        display: flex;
        gap: 4px;
      }
      .dots i {
        width: 16px;
        height: 6px;
        background: var(--ka-stone-600);
      }
      .dots i.on {
        background: var(--ka-gold);
      }
      h2 {
        font-size: 1.25rem;
        margin: 0;
      }
      h2:focus {
        outline: none;
      }
      .small {
        font-size: 0.85rem;
        margin: 0;
      }
      /* Controls a step may add to the card (a name field, a checkbox). */
      label {
        display: grid;
        gap: 0.35rem;
        font-weight: 600;
      }
      label.check {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-height: 44px;
      }
      input[type='text'] {
        font: inherit;
        min-height: 48px;
        padding: 0 0.75rem;
        background: var(--ka-stone-900);
        color: var(--ka-parchment);
        border: 2px solid var(--ka-stone-400);
      }
      input[type='checkbox'] {
        width: 24px;
        height: 24px;
        margin: 0;
      }
      .foot {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
    `,
  ];

  constructor() {
    super();
    this.steps = [];
    this.open = false;
    this.flow = null;
    this.box = null;
    this.cardTop = false;
    this.placement = 'auto';
  }

  /** The activity reports that step `id` is done (ignored unless it is the current step). */
  complete(id: string) {
    const f = this.flow;
    if (f && this.steps[f.index]?.id === id) this.go(completeStep(f, f.index));
  }

  private get step() {
    return this.flow ? this.steps[this.flow.index] : undefined;
  }

  private go(next: FlowState) {
    this.flow = next;
    if (!next.finished) return;
    this.open = false;
    this.dispatchEvent(
      new CustomEvent('ka-walkthrough-end', {
        detail: { skipped: next.finished === 'skipped' },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('open')) {
      if (this.open) {
        this.flow = startFlow(this.steps.length);
        this.shown = -1;
      } else this.flow = null;
    }
  }

  override updated() {
    if (this.open && !this.raf) this.measure();
    const f = this.flow;
    if (!f || f.finished || f.index === this.shown) return;
    this.shown = f.index;
    const step = this.steps[f.index]!;
    this.dispatchEvent(
      new CustomEvent('ka-walkthrough-step', {
        detail: { index: f.index, id: step.id },
        bubbles: true,
        composed: true,
      }),
    );
    const t = step.target?.();
    if (t instanceof HTMLElement && t.tabIndex >= 0) t.focus();
    else this.renderRoot.querySelector<HTMLElement>('h2')?.focus();
  }

  /** Keeps the spotlight on the target every frame while open (it may move, resize or rotate). */
  private measure = () => {
    if (!this.open) {
      this.raf = 0;
      return;
    }
    const r = this.step?.target?.()?.getBoundingClientRect();
    const box = r
      ? { left: r.left - 6, top: r.top - 6, width: r.width + 12, height: r.height + 12 }
      : null;
    const b = this.box;
    if (
      !b !== !box ||
      (b &&
        box &&
        (b.left !== box.left ||
          b.top !== box.top ||
          b.width !== box.width ||
          b.height !== box.height))
    )
      this.box = box;
    const cardH = this.renderRoot.querySelector('.card')?.getBoundingClientRect().height ?? 0;
    // Move the card up only when the target sits low and there is room above it.
    const top = !!r && r.bottom > innerHeight - cardH - 24 && r.top > cardH + 32;
    const pinned = this.placement === 'top' || top;
    if (pinned !== this.cardTop) this.cardTop = pinned;
    this.raf = requestAnimationFrame(this.measure);
  };

  override disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private press() {
    const f = this.flow!;
    const step = this.step!;
    if (step.waitForActivity)
      this.dispatchEvent(
        new CustomEvent('ka-walkthrough-action', {
          detail: { id: step.id },
          bubbles: true,
          composed: true,
        }),
      );
    else this.go(completeStep(f, f.index));
  }

  override render() {
    const f = this.flow;
    const step = this.step;
    if (!this.open || !f || f.finished || !step) return nothing;
    const b = this.box;
    return html`<div class="layer">
      ${
        b
          ? html`<div
              class="hole"
              style="left:${b.left}px;top:${b.top}px;width:${b.width}px;height:${b.height}px"
            ></div>`
          : html`<div class="dim"></div>`
      }
      <section
        class="panel card ${this.cardTop ? 'top' : ''}"
        role="dialog"
        aria-label="How to play"
      >
        <div class="dots" aria-hidden="true">
          ${this.steps.map((_, i) => html`<i class=${i <= f.index ? 'on' : ''}></i>`)}
        </div>
        <h2 tabindex="-1" aria-live="polite">${step.say}</h2>
        ${step.hint ? html`<p class="small muted">${step.hint}</p>` : nothing}
        ${step.body?.() ?? nothing}
        <div class="foot">
          <button class="btn" ?disabled=${f.index === 0} @click=${() => this.go(backStep(f))}>
            Back
          </button>
          ${
            step.button
              ? html`<button
                  class="btn ${step.button === 'Save it' ? 'primary' : 'green'}"
                  @click=${this.press}
                >
                  ${step.button}
                </button>`
              : html`<span class="small muted">waiting for you…</span>`
          }
          <button class="link" @click=${() => this.go(skipFlow(f))}>Skip</button>
        </div>
      </section>
    </div>`;
  }
}
customElements.define('ka-walkthrough', KaWalkthrough);
